import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { BookOpen, Clock, ChevronLeft, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable in-app Knowledge Base.
 * Props:
 *   audience: 'executive' | 'brand'
 *   onClose?: () => void   (optional — when used in a modal/drawer)
 */
export default function KnowledgeBase({ audience, onClose }) {
  const [articles, setArticles] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeArticle, setActiveArticle] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/kb/articles`, { params: { audience } })
      .then(r => setArticles(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [audience]);

  useEffect(() => {
    if (!activeId) { setActiveArticle(null); return; }
    axios.get(`${API}/kb/articles/${activeId}`)
      .then(r => setActiveArticle(r.data))
      .catch(() => setActiveArticle(null));
  }, [activeId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter(a =>
      a.title.toLowerCase().includes(q) ||
      a.summary.toLowerCase().includes(q) ||
      a.category.toLowerCase().includes(q)
    );
  }, [articles, search]);

  const grouped = useMemo(() => {
    const out = {};
    for (const a of filtered) {
      (out[a.category] = out[a.category] || []).push(a);
    }
    return out;
  }, [filtered]);

  if (activeArticle) {
    return (
      <div className="space-y-4" data-testid="kb-article-view">
        <button onClick={() => { setActiveId(null); setActiveArticle(null); }} className="text-xs text-[#9CA3AF] hover:text-white flex items-center gap-1" data-testid="kb-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to articles
        </button>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wider text-[#FF4343] mb-1">{activeArticle.category}</p>
          <h2 className="font-display text-2xl font-bold text-white">{activeArticle.title}</h2>
          <p className="text-[10px] text-[#6B7280] mt-2 flex items-center gap-1"><Clock className="w-3 h-3" /> {activeArticle.minutes || 2} min read</p>
        </div>
        <article className="prose prose-invert max-w-none text-sm leading-relaxed text-[#9CA3AF]" data-testid="kb-article-body">
          {renderMarkdown(activeArticle.body)}
        </article>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kb-list-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-white">Help &amp; <span className="text-[#FF4343]">Knowledge Base</span></h2>
          <p className="text-[10px] text-[#6B7280] mt-1">{audience === 'executive' ? 'Quick guides for executives' : 'Quick guides for brands'} · {articles.length} articles</p>
        </div>
        {onClose && <button onClick={onClose} className="text-xs text-[#9CA3AF] hover:text-white">Close</button>}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#6B7280]" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search articles..."
          data-testid="kb-search"
          className="w-full pl-9 pr-3 py-2 bg-[#111827] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
      </div>

      {loading ? <p className="text-[#9CA3AF] text-sm">Loading articles…</p>
        : Object.keys(grouped).length === 0 ? <p className="text-[#9CA3AF] text-sm">No articles match your search.</p>
          : Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} className="space-y-2">
              <h3 className="text-[10px] font-mono uppercase tracking-wider text-[#FF4343]">{cat}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {items.map(a => (
                  <button key={a.id} onClick={() => setActiveId(a.id)} data-testid={`kb-article-${a.id}`}
                    className="text-left bg-[#111827] border border-white/10 hover:border-[#FF4343]/40 p-3 transition-colors">
                    <div className="flex items-center gap-2 mb-1">
                      <BookOpen className="w-3.5 h-3.5 text-[#FF4343]" />
                      <span className="text-sm font-semibold text-white">{a.title}</span>
                    </div>
                    <p className="text-[10px] text-[#9CA3AF] line-clamp-2">{a.summary}</p>
                    <p className="text-[10px] text-[#6B7280] mt-2 flex items-center gap-1"><Clock className="w-3 h-3" /> {a.minutes || 2} min</p>
                  </button>
                ))}
              </div>
            </div>
          ))}
    </div>
  );
}

// Minimal markdown renderer — supports h2/h3, bold, italic, code, lists, blockquotes, tables, paragraphs.
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const out = [];
  let listBuf = [];
  let tableBuf = [];
  let inCode = false;
  let codeBuf = [];

  const flushList = () => {
    if (listBuf.length) {
      out.push(<ul key={`ul-${out.length}`} className="list-disc pl-5 space-y-1 my-2">{listBuf.map((li, i) => <li key={i}>{inlineFmt(li)}</li>)}</ul>);
      listBuf = [];
    }
  };
  const flushTable = () => {
    if (tableBuf.length >= 2) {
      const head = tableBuf[0].split('|').map(c => c.trim()).filter(Boolean);
      const rows = tableBuf.slice(2).map(r => r.split('|').map(c => c.trim()).filter(Boolean));
      out.push(
        <table key={`tbl-${out.length}`} className="my-3 w-full text-xs border border-white/10">
          <thead><tr>{head.map((h, i) => <th key={i} className="px-2 py-1 text-left bg-white/5 text-white border-b border-white/10">{inlineFmt(h)}</th>)}</tr></thead>
          <tbody>{rows.map((r, ri) => <tr key={ri}>{r.map((c, ci) => <td key={ci} className="px-2 py-1 border-b border-white/5">{inlineFmt(c)}</td>)}</tr>)}</tbody>
        </table>
      );
    }
    tableBuf = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (l.trim().startsWith('```')) {
      if (inCode) { out.push(<pre key={`pre-${out.length}`} className="bg-white/5 p-3 text-xs font-mono overflow-x-auto my-2">{codeBuf.join('\n')}</pre>); codeBuf = []; inCode = false; }
      else { flushList(); flushTable(); inCode = true; }
      continue;
    }
    if (inCode) { codeBuf.push(l); continue; }
    if (l.startsWith('## ')) { flushList(); flushTable(); out.push(<h3 key={`h2-${out.length}`} className="font-display text-lg font-bold text-white mt-4 mb-1">{inlineFmt(l.slice(3))}</h3>); continue; }
    if (l.startsWith('### ')) { flushList(); flushTable(); out.push(<h4 key={`h3-${out.length}`} className="text-sm font-semibold text-white mt-3 mb-1">{inlineFmt(l.slice(4))}</h4>); continue; }
    if (l.startsWith('> ')) { flushList(); flushTable(); out.push(<blockquote key={`bq-${out.length}`} className="border-l-2 border-[#FF4343] pl-3 italic text-[#9CA3AF] my-2">{inlineFmt(l.slice(2))}</blockquote>); continue; }
    if (l.match(/^[-*]\s/)) { flushTable(); listBuf.push(l.replace(/^[-*]\s+/, '')); continue; }
    if (l.match(/^\d+\.\s/)) { flushTable(); listBuf.push(l.replace(/^\d+\.\s+/, '')); continue; }
    if (l.includes('|')) { flushList(); tableBuf.push(l); continue; }
    flushList(); flushTable();
    if (l.trim()) out.push(<p key={`p-${out.length}`} className="my-2">{inlineFmt(l)}</p>);
  }
  flushList(); flushTable();
  return out;
}

function inlineFmt(s) {
  if (!s) return s;
  // **bold** and *italic* and `code` (very lightweight)
  const parts = [];
  let buf = '';
  let i = 0;
  while (i < s.length) {
    if (s.startsWith('**', i)) {
      const end = s.indexOf('**', i + 2);
      if (end > -1) { if (buf) { parts.push(buf); buf = ''; } parts.push(<strong key={parts.length} className="text-white">{s.slice(i + 2, end)}</strong>); i = end + 2; continue; }
    }
    if (s[i] === '`') {
      const end = s.indexOf('`', i + 1);
      if (end > -1) { if (buf) { parts.push(buf); buf = ''; } parts.push(<code key={parts.length} className="px-1 bg-white/10 text-[#FF4343] text-[10px]">{s.slice(i + 1, end)}</code>); i = end + 1; continue; }
    }
    buf += s[i]; i++;
  }
  if (buf) parts.push(buf);
  return parts;
}
